import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';

/**
 * This function will smoothly scroll the window to the given element if the element is
 * not into view already. It will account for virtualization and keep scrolling until the element is definitely
 * in view.
 */
export async function scrollToVirtualNode(element: HTMLElement): Promise<void> {
    // we want to scroll using the nearest virtual parent since the content of the virtual node may change
    // once it gets mounted as a real node. By keeing the reference to the parent, we ensure we never lose that
    // node's reference or keep a reference to a delete node alive.
    const findScrollTo = () => {
        let scrollTo: HTMLElement | null = element;
        while (scrollTo) {
            scrollTo = scrollTo.parentElement;

            if (
                scrollTo === document.querySelector('body') ||
                scrollTo?.hasAttribute('data-virtualparent')
            ) {
                break;
            }
        }

        return scrollTo;
    };

    let intersectionObserver: IntersectionObserver;

    // Return a promise so we can track on the scrolling is done.
    return new Promise((resolve) => {
        const scrollTo = findScrollTo();
        if (!scrollTo || !scrollTo.firstChild) {
            return;
        }

        const complete = () => {
            intersectionObserver.disconnect();
            resolve();
        };

        let hasIntersected = false;
        // Start observing the elements when scrolling, they may jump around as things get virtualized
        // and we need to make sure we scroll down to the correct element.
        intersectionObserver = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]) => {
                const [observed] = entries;

                if (observed?.isIntersecting) {
                    // Make sure the elements are still intersecting, virtualization may have moved then.
                    requestAnimationFrame(() => {
                        complete();
                        hasIntersected = true;
                    });
                }
            },
            {
                /**
                 * This rootMargin creates a horizontal line vertically centered
                 * that will help trigger an intersection at the very center rather than at the top.
                 */
                rootMargin: '-50% 0% -50% 0%',
            }
        );

        // Keep scrolling on a slow loop, we only want to stop once we know for sure the element is truly in view.
        const keepScrolling = () => {
            scrollIntoViewIfNeeded(scrollTo, {
                scrollMode: 'if-needed',
                block: 'center',
                inline: 'center',
                behavior: 'smooth',
            });
            setTimeout(() => {
                if (!hasIntersected) {
                    keepScrolling();
                }
            }, 500);
        };

        keepScrolling();

        intersectionObserver.observe(scrollTo);
    });
}
